// CallRecordings.jsx
import React, { useState } from 'react';
import ApiClient from '../auth/AuthService';
import { APP_VERSION } from '../config';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Divider,
  CircularProgress,
  TextField,
} from '@mui/material';
import { Search, Download } from '@mui/icons-material';

// Должно совпадать с MAX_RANGE_DAYS в client_communication/call_recordings.py.
// Бэкенд проверяет диапазон независимо от фронтенда.
const MAX_RANGE_DAYS = 7;

// Порог длительности зашит в бэкенде (MIN_RECORDING_DURATION_SECONDS).
// Здесь он используется только в тексте подсказки.
const MIN_DURATION_SECONDS = 45;

// Поиск ходит в Twilio и может быть заметно медленнее обычного запроса.
const PREVIEW_REQUEST_TIMEOUT_MS = 120000;

// Даты выбираются в America/Los_Angeles, поэтому и значения по умолчанию
// считаются по калифорнийскому календарю, а не по локальному времени браузера.
const laToday = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  return parts; // en-CA даёт формат YYYY-MM-DD
};

const shiftDays = (isoDate, days) => {
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

const formatBytes = (bytes) => {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Разбирает тело ответа об ошибке и всегда показывает код статуса.
// Ответ может быть JSON от DRF ({"detail": ...} или {"end_date": [...]}),
// а может быть HTML от прокси — тогда показываем начало текста.
const describeResponseError = async (response) => {
  const prefix = `HTTP ${response.status} ${response.statusText || ''}`.trim();
  let body = '';

  try {
    body = await response.text();
  } catch (readError) {
    return `${prefix} — response body could not be read (${readError.message})`;
  }

  if (!body) {
    return prefix;
  }

  try {
    const data = JSON.parse(body);
    if (typeof data === 'string') {
      return `${prefix} — ${data}`;
    }
    if (data && data.detail) {
      return `${prefix} — ${data.detail}`;
    }
    if (data && typeof data === 'object') {
      const parts = Object.keys(data).map(
        (field) => `${field}: ${[].concat(data[field]).join(' ')}`
      );
      if (parts.length > 0) {
        return `${prefix} — ${parts.join('; ')}`;
      }
    }
  } catch (parseError) {
    // не JSON — показываем начало тела как есть
    return `${prefix} — ${body.slice(0, 300)}`;
  }

  return prefix;
};

// Имя файла из Content-Disposition; при работе с другого origin заголовок
// может быть недоступен, тогда используется имя, полученное от preview.
const fileNameFromResponse = (response, fallback) => {
  const header = response.headers.get('Content-Disposition') || '';
  const match = /filename="?([^";]+)"?/i.exec(header);
  return (match && match[1]) || fallback;
};

const saveBlob = (blob, fileName) => {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  // download обязателен: без него клик по ссылке станет переходом по адресу,
  // а не сохранением файла.
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
};

// Число календарных дней в диапазоне, обе границы включительно.
const inclusiveDayCount = (start, end) => {
  const startMs = Date.parse(`${start}T00:00:00Z`);
  const endMs = Date.parse(`${end}T00:00:00Z`);
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
    return null;
  }
  return Math.round((endMs - startMs) / 86400000) + 1;
};

const CallRecordings = ({ user }) => {
  const [apiClient] = useState(ApiClient());

  const defaultEnd = laToday();
  const defaultStart = shiftDays(defaultEnd, -6);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);

  const [preview, setPreview] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);

  const [isDownloading, setIsDownloading] = useState(false);
  const [receivedBytes, setReceivedBytes] = useState(0);
  const [downloadResult, setDownloadResult] = useState(null);
  const [downloadError, setDownloadError] = useState(null);

  const dayCount = inclusiveDayCount(startDate, endDate);

  let rangeError = null;
  if (dayCount === null) {
    rangeError = 'Please select both dates.';
  } else if (dayCount < 1) {
    rangeError = 'End date must not be before start date.';
  } else if (dayCount > MAX_RANGE_DAYS) {
    rangeError = `The date range must not exceed ${MAX_RANGE_DAYS} calendar days `
      + `(inclusive). Currently selected: ${dayCount} days.`;
  }

  const isRangeValid = rangeError === null;

  // Любое изменение дат обесценивает найденный результат.
  const invalidatePreview = () => {
    setPreview(null);
    setError(null);
    setDownloadError(null);
    setDownloadResult(null);
    setReceivedBytes(0);
  };

  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
    invalidatePreview();
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
    invalidatePreview();
  };

  // Ошибка axios в читаемом виде: код статуса + разобранное тело ответа DRF.
  const describeAxiosError = (err, fallback) => {
    const response = err.response;

    if (!response) {
      // Сети не было вовсе (таймаут, обрыв, CORS).
      return err.message || fallback;
    }

    const prefix = `HTTP ${response.status} ${response.statusText || ''}`.trim();
    const data = response.data;

    if (typeof data === 'string' && data) {
      return `${prefix} — ${data.slice(0, 300)}`;
    }
    if (data && data.detail) {
      return `${prefix} — ${data.detail}`;
    }
    if (data && typeof data === 'object') {
      const parts = Object.keys(data).map(
        (field) => `${field}: ${[].concat(data[field]).join(' ')}`
      );
      if (parts.length > 0) {
        return `${prefix} — ${parts.join('; ')}`;
      }
    }

    return `${prefix} — ${err.message || fallback}`;
  };

  const handleSearch = async () => {
    setIsSearching(true);
    setError(null);
    setPreview(null);
    setDownloadError(null);
    setDownloadResult(null);
    setReceivedBytes(0);
    try {
      const response = await apiClient.post(
        '/api/call-recordings/preview/',
        { start_date: startDate, end_date: endDate },
        { timeout: PREVIEW_REQUEST_TIMEOUT_MS }
      );
      setPreview(response);
    } catch (err) {
      console.error('Error searching call recordings:', err);
      setError(describeAxiosError(err, 'Failed to search recordings. Please try again.'));
    } finally {
      setIsSearching(false);
    }
  };

  // Скачивание идёт обычным XHR с заголовком Authorization, а файл сохраняется
  // из полученного потока.
  //
  // Переходом по ссылке (window.location / клик по <a href>) это делать нельзя:
  // такая навигация не передаёт заголовок Authorization, а дев-сервер CRA
  // запросы с Accept: text/html не проксирует и отдаёт index.html — React
  // Router не находит маршрут /api/... и по catch-all уходит на страницу по
  // умолчанию. Именно так страница и "убегала" на /leads вместо загрузки.
  const handleDownload = async () => {
    if (!preview) {
      return;
    }

    setDownloadError(null);
    setDownloadResult(null);
    setReceivedBytes(0);
    setIsDownloading(true);

    const base = (apiClient.baseURL() || '').replace(/\/+$/, '');
    const url = `${base}/api/call-recordings/download/`
      + `?start_date=${encodeURIComponent(preview.start_date)}`
      + `&end_date=${encodeURIComponent(preview.end_date)}`;

    const accessToken = sessionStorage.getItem(`access_token_${APP_VERSION}`);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });

      if (!response.ok) {
        const description = await describeResponseError(response);
        if (response.status === 401) {
          throw new Error(`${description} (your session has expired — reload the page and log in again)`);
        }
        throw new Error(description);
      }

      // Тело читается по кускам: длина архива заранее неизвестна, поэтому
      // прогресс показывается в принятых байтах.
      let blob;
      if (response.body && typeof response.body.getReader === 'function') {
        const reader = response.body.getReader();
        const chunks = [];
        let received = 0;

        for (;;) {
          // eslint-disable-next-line no-await-in-loop
          const { done, value } = await reader.read();
          if (done) {
            break;
          }
          chunks.push(value);
          received += value.length;
          setReceivedBytes(received);
        }

        blob = new Blob(chunks, { type: 'application/zip' });
      } else {
        blob = await response.blob();
        setReceivedBytes(blob.size);
      }

      saveBlob(blob, fileNameFromResponse(response, preview.file_name));
      setDownloadResult({ size: blob.size });
    } catch (err) {
      console.error('Error downloading call recordings:', err);
      setDownloadError(err.message || 'Download failed.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Box mt={4}>
        <Container maxWidth="lg">
          <Paper elevation={3}>
            <Box sx={{ padding: 3 }}>
              <Typography variant="h5" gutterBottom>Call Recordings</Typography>

              <Typography variant="body2" sx={{ marginBottom: 2 }}>
                Downloads Twilio call recordings for the selected date range as a single
                ZIP archive. Dates are interpreted in <strong>America/Los_Angeles</strong>,
                both days included. Only completed calls longer
                than {MIN_DURATION_SECONDS} seconds are included. The range may cover at
                most {MAX_RANGE_DAYS} calendar days.
              </Typography>

              <Divider sx={{ my: 3 }} />

              {/* Диапазон дат */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  marginBottom: 2,
                }}
              >
                <TextField
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  sx={{ margin: { xs: '0 0 1rem 0', sm: '0 1rem 0 0' } }}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={handleEndDateChange}
                  sx={{ margin: { xs: '0 0 1rem 0', sm: '0 1rem 0 0' } }}
                  InputLabelProps={{ shrink: true }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Search />}
                  onClick={handleSearch}
                  disabled={!isRangeValid || isSearching}
                  sx={{ margin: { xs: '0 0 1rem 0', sm: '0 1rem 0 0' } }}
                >
                  Find recordings
                </Button>
                {isSearching && <CircularProgress size={24} sx={{ marginLeft: 1 }} />}
              </Box>

              {rangeError && (
                <Typography variant="body1" color="error" sx={{ marginBottom: 2 }}>
                  {rangeError}
                </Typography>
              )}

              {error && (
                <Typography
                  variant="body1"
                  color="error"
                  sx={{ marginBottom: 2, whiteSpace: 'pre-wrap' }}
                >
                  {error}
                </Typography>
              )}

              {isSearching && (
                <Typography variant="body2" sx={{ marginBottom: 2 }}>
                  Reading recording metadata from Twilio. No audio is downloaded yet.
                </Typography>
              )}

              {preview && (
                <>
                  <Divider sx={{ my: 3 }} />

                  <Typography variant="h6" gutterBottom>Search result</Typography>

                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'blue' }}>
                    {preview.count} recordings, approx. {preview.estimated_size_display}.
                  </Typography>
                  <Typography variant="body2" sx={{ marginTop: 1, marginBottom: 2 }}>
                    Range {preview.start_date} .. {preview.end_date} (America/Los_Angeles).
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<Download />}
                      onClick={handleDownload}
                      disabled={preview.count === 0 || isDownloading}
                    >
                      Download ZIP
                    </Button>
                    {isDownloading && <CircularProgress size={24} sx={{ marginLeft: 2 }} />}
                  </Box>

                  {preview.count === 0 && (
                    <Typography variant="body1">
                      Nothing to download for this range.
                    </Typography>
                  )}

                  {isDownloading && (
                    <>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        Downloading… {formatBytes(receivedBytes)} received.
                      </Typography>
                      <Typography variant="body2" sx={{ marginTop: 1 }}>
                        The archive is built on the server while it is being sent, so
                        the total size is not known in advance. Please keep this page
                        open until it finishes.
                      </Typography>
                    </>
                  )}

                  {downloadResult && (
                    <>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'green' }}>
                        Done — {formatBytes(downloadResult.size)} saved
                        as {preview.file_name}.
                      </Typography>
                      <Typography variant="body2" sx={{ marginTop: 1 }}>
                        The final summary (downloaded / skipped) is inside the archive
                        as <code>download_summary.txt</code>.
                      </Typography>
                    </>
                  )}

                  {downloadError && (
                    <Typography
                      variant="body1"
                      color="error"
                      sx={{ marginTop: 1, whiteSpace: 'pre-wrap' }}
                    >
                      {downloadError}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default CallRecordings;
